import {DOCUMENT} from '@angular/common';
import {afterNextRender, Component, inject, Injector, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {TourMatMenuModule, TourService, TourState, TourStepTemplateComponent} from 'ngx-ui-tour-md-menu';

@Component({
    selector: 'app-walkthrough-step',
    imports: [TourMatMenuModule, MatCardModule, MatButtonModule, MatIconModule],
    template: `
        <tour-step-template>
            <ng-template let-step="step">
                <mat-card class="page-guide" (click)="$event.stopPropagation()" [attr.aria-label]="step.title">
                    <div class="guide-heading">
                        <mat-card-title>{{ step.title }}</mat-card-title>
                        <button mat-icon-button type="button" (click)="tour.end()" aria-label="Close page guide" data-page-guide-close><mat-icon>close</mat-icon></button>
                    </div>
                    <mat-card-content [innerHTML]="step.content"></mat-card-content>
                    <mat-card-actions>
                        <button mat-button type="button" (click)="tour.prev()" [disabled]="!tour.hasPrev(step)">Back</button>
                        <span class="guide-count">{{ tour.steps.indexOf(step) + 1 }} of {{ tour.steps.length }}</span>
                        @if (tour.hasNext(step)) {
                            <button mat-button type="button" (click)="tour.end()">Maybe later</button>
                            <button mat-button type="button" (click)="tour.next()">Next</button>
                        } @else {
                            <button mat-button type="button" (click)="tour.end()">Done</button>
                        }
                    </mat-card-actions>
                </mat-card>
            </ng-template>
        </tour-step-template>
    `,
    styles: [`
        .page-guide { width: 360px; max-width: calc(100vw - 32px); max-height: calc(100dvh - 32px); }
        .guide-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 16px; }
        mat-card-title { font-size: 16px; line-height: 1.4; }
        .guide-heading button { flex: none; }
        mat-card-content { overflow-y: auto; font-size: 14px; line-height: 1.5; }
        mat-card-actions { flex-wrap: wrap; padding: 8px; gap: 4px; }
        .guide-count { margin-right: auto; font-size: 12px; }
    `],
})
export class WalkthroughStepComponent {
    public readonly tour = inject(TourService);
    private readonly document = inject(DOCUMENT);
    private readonly injector = inject(Injector);
    @ViewChild(TourStepTemplateComponent) private template: TourStepTemplateComponent;
    private origin?: HTMLElement;

    public constructor() {
        this.tour.start$.pipe(takeUntilDestroyed()).subscribe(() => this.origin = this.document.activeElement as HTMLElement);
        this.tour.stepShow$.pipe(takeUntilDestroyed()).subscribe(() => {
            afterNextRender(() => {
                if (this.tour.getStatus() !== TourState.ON) return;
                this.document.getElementById(this.template.tourStep.panelId)?.querySelector<HTMLButtonElement>('[data-page-guide-close]')?.focus();
            }, {injector: this.injector});
        });
        this.tour.end$.pipe(takeUntilDestroyed()).subscribe(() => {
            afterNextRender(() => {
                const active = this.document.activeElement;
                if (active === this.document.body || active?.closest('.page-guide')) this.origin?.focus();
                this.origin = undefined;
            }, {injector: this.injector});
        });
    }
}
